// 📄 components/StepAPTesting.tsx
import React, { useState } from 'react';
import { Play, RefreshCw, CheckCircle2, ShieldAlert } from 'lucide-react';
import { GeneralsFormData } from '../../../../../services/commerce/integrationService.ts';

interface StepAPTestingProps {
  apTestTargetUrl: string;
  onChange: (field: keyof GeneralsFormData, value: any) => void;
  isInvalid: boolean;
}

export function StepAPTesting({
  apTestTargetUrl,
  onChange,
  isInvalid
}: StepAPTestingProps) {
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<'idle' | 'success' | 'error'>('idle');

  const runTest = () => {
    setTesting(true);
    setTestResult('idle');
    setTimeout(() => {
      setTesting(false);
      if (apTestTargetUrl.trim().startsWith('http')) {
        setTestResult('success');
      } else {
        setTestResult('error');
      }
    }, 2000);
  };

  return (
    <div className="space-y-4 animate-fadeIn">
      <div className="bg-amber-500/5 border border-amber-500/10 rounded-xl p-3 space-y-1">
        <span className="text-[10px] font-bold text-amber-400 uppercase tracking-widest block">SAP Ariba Buyer Portal</span>
        <p className="font-mono text-xs text-gray-300">
          <span>Ariba Administrator → Core Administration → Integration Manager → Integration Test Tool</span>
        </p>
      </div>

      <div>
        <h3 className="text-lg font-medium text-amber-400">3. Test de Conectividad End-to-End</h3>
        <p className="text-sm text-gray-400 mt-1">
          Valida que el portal de Ariba pueda enviar un ping de prueba a través de Managed Gateway y Cloud Connector hasta los endpoints de tu ERP.
        </p>
      </div>

      <div className="bg-white/5 p-4 rounded-xl border border-white/5 space-y-4">
        <div>
          <label className="block text-xs font-medium text-gray-400 mb-1">
            Endpoint URL de Prueba (CIG Inbound Endpoint) <span className="text-red-400">*</span>
          </label>
          <input
            type="text"
            value={apTestTargetUrl}
            onChange={(e) => onChange('apTestTargetUrl', e.target.value)}
            placeholder="ej. https://test-integration.us.managedgateway.cloud.sap/cxf/receiveIDOC"
            className={`w-full bg-zinc-800 border rounded-lg p-2.5 text-sm font-mono text-white focus:outline-none focus:border-amber-500 ${
              isInvalid && !apTestTargetUrl ? 'border-red-500' : 'border-white/10'
            }`}
          />
        </div>
      </div>

      <div className="bg-zinc-900/40 border border-white/5 rounded-xl p-4 flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="space-y-1 text-center sm:text-left">
          <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider block">Verificar Handshake de Conexión</span>
          <p className="text-xs text-gray-400">
            Envía una carga SOAP XML vacía para probar la autenticación y enrutamiento hacia Managed Gateway.
          </p>
        </div>
        
        <button
          type="button"
          onClick={runTest}
          disabled={testing}
          className="flex items-center gap-2 bg-amber-600 hover:bg-amber-500 text-[#fff] font-medium text-xs px-4 py-2.5 rounded-lg shadow-md transition-all disabled:bg-gray-600 active:scale-[0.98]"
        >
          {testing ? (
            <>
              <RefreshCw className="w-3.5 h-3.5 animate-spin" />
              Probando canal...
            </>
          ) : (
            <>
              <Play className="w-3.5 h-3.5" />
              Probar Canal
            </>
          )}
        </button>
      </div>

      {testResult === 'success' && (
        <div className="bg-green-500/10 border border-green-500/20 text-green-400 p-4 rounded-xl flex items-center gap-3 text-sm animate-fadeIn">
          <CheckCircle2 className="w-5 h-5 flex-shrink-0" />
          <div>
            <span className="font-semibold block">Conexión Aprobada</span>
            <span className="text-xs text-green-500/80">
              La conexión entre Ariba Portal y CIG es correcta. HTTP 200/OK retornado exitosamente.
            </span>
          </div>
        </div>
      )}

      {testResult === 'error' && (
        <div className="bg-red-500/10 border border-red-500/20 text-red-400 p-4 rounded-xl flex items-center gap-3 text-sm animate-fadeIn">
          <ShieldAlert className="w-5 h-5 flex-shrink-0" />
          <div>
            <span className="font-semibold block">Handshake Fallido</span>
            <span className="text-xs text-red-500/80">
              Por favor introduce un Endpoint válido que comience con http/https. Si persiste, valida las credenciales de Ariba CIG User.
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
